import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UtensilsCrossed, ImagePlus, X } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

export function TodaysMenuCard() {
  const [image, setImage] = useState("");
  const [comment, setComment] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    apiClient.get("/customer-updates/todays-menu").then(({ data }) => {
      setImage(data.image_url ? data.image_url.replace(/^.*\/daily-menu\/images\//, "") : "");
      setPreviewUrl(fullUrl(data.image_url) || "");
      setComment(data.comment || "");
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    const body = new FormData();
    body.append("image", file);
    try {
      const { data } = await apiClient.post("/daily-menu/upload-image", body);
      setImage(data.path);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not upload the image."));
      setPreviewUrl("");
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => { setImage(""); setPreviewUrl(""); if (fileRef.current) fileRef.current.value = ""; };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.post("/customer-updates/todays-menu", { image: image || null, comment: comment || null });
      toast.success("Today's menu updated — customers will see it now.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not save today's menu."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="todays-menu-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
            <UtensilsCrossed size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Today's Menu</p>
            <p className="mt-0.5 text-xs text-text-muted">Shown at the top of every customer's Updates tab. Resets automatically at midnight.</p>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label>Menu photo</Label>
            {previewUrl ? (
              <div className="relative w-full max-w-[200px]">
                <img src={previewUrl} alt="Today's menu" data-testid="todays-menu-preview" className="aspect-square w-full rounded-md border border-border object-cover" />
                <button type="button" onClick={clearImage} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-danger text-white shadow" data-testid="todays-menu-image-remove">
                  <X size={13} strokeWidth={2} />
                </button>
              </div>
            ) : (
              <label htmlFor="todays-menu-input" className="flex h-28 w-full max-w-[200px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-surface text-text-muted hover:bg-background">
                <ImagePlus size={18} strokeWidth={1.5} />
                <span className="text-xs">Upload square photo</span>
              </label>
            )}
            <input ref={fileRef} id="todays-menu-input" type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="todays-menu-input" />
            {uploading && <p className="text-xs text-text-muted">Uploading...</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="todays-menu-comment">Caption (optional)</Label>
            <Textarea id="todays-menu-comment" data-testid="todays-menu-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g. Rajma Chawal, Salad & Raita" maxLength={255} />
          </div>

          <Button type="button" variant="accent" className="w-full" onClick={save} disabled={saving || uploading || !loaded} data-testid="todays-menu-save">
            {saving ? "Saving..." : "Set Today's Menu"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
